import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getNotificationsHistory } from "@src/entities/notifications/api/request/getNotificationsHistory";
import { mapServerEventToUi } from "@src/entities/notifications/model/mappers/mapServerEventToUi";
import { useNotificationsStore } from "@src/entities/notifications/model/store/notificationsStore";
import { useUserInfoMeStore } from "@src/entities/user/model/store/userInfoMeStore";

const PAGE_SIZE = 10;
const INITIAL_OFFSET = 10;

export const useNotificationsHistory = (enabled: boolean) => {
  const wsItems = useNotificationsStore((s) => s.items);
  const user = useUserInfoMeStore((s) => s.user);

  const query = useInfiniteQuery({
    queryKey: ["notifications-history"],
    queryFn: async ({ pageParam = INITIAL_OFFSET }) => {
      const response = await getNotificationsHistory(
        pageParam,
        PAGE_SIZE,
        user?.id || 0,
      );

      return {
        items: response.items
          .map((dto) => mapServerEventToUi(dto))
          .filter(Boolean),
        total: response.total,
        nextOffset: pageParam + PAGE_SIZE,
      };
    },
    initialPageParam: INITIAL_OFFSET,
    getNextPageParam: (lastPage, allPages) => {
      const loadedHistoryCount = allPages.reduce(
        (acc, page) => acc + page.items.length,
        0,
      );

      const totalCount = lastPage.total;
      const alreadyShown = wsItems.length + loadedHistoryCount;

      return alreadyShown < totalCount ? lastPage.nextOffset : undefined;
    },
    enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const historyItems = useMemo(() => {
    return query.data?.pages.flatMap((page) => page.items) ?? [];
  }, [query.data]);

  const combinedItems = useMemo(() => {
    const map = new Map<string, any>();

    wsItems.forEach((item) => map.set(item.id, item));
    historyItems.forEach((item) => {
      if (item && !map.has(item.id)) {
        map.set(item.id, item);
      }
    });

    return Array.from(map.values());
  }, [wsItems, historyItems]);

  const total = query.data?.pages[0]?.total ?? wsItems.length;

  return {
    items: combinedItems,
    total,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
  };
};
